import { t, ui, uiError } from '../i18n';
import { getWebappApiUrl, webappFetch } from '../utils/apiConfig';
import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Alert,
  CircularProgress,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Tooltip,
  Stack
} from '@mui/material';
import {
  AccountBalance as InstitutionIcon,
  Computer as ComputerIcon,
  DesktopWindows as WorkstationIcon,
  Dns as ServerIcon,
  LocalShipping as TruckIcon,
  Storage as StorageIcon,
  Refresh as RefreshIcon,
  AccountTree as ChainIcon,
  Lan as LanIcon
} from '@mui/icons-material';
import { mapOrganizationName } from '../utils/organizationMapping';

const ORGANIZATION_ORDER = ['KULSMSP', 'KAISTMSP', 'EINSMSP'];

const ORGANIZATION_META = {
  OrdererMSP: {
    label: 'Ordering Service',
    accent: '#5e35b1',
    background: 'rgba(94, 53, 177, 0.06)'
  },
  KULSMSP: {
    label: 'KULS',
    accent: '#1976d2',
    background: 'rgba(25, 118, 210, 0.06)'
  },
  KAISTMSP: {
    label: 'KAIST',
    accent: '#2e7d32',
    background: 'rgba(46, 125, 50, 0.06)'
  },
  EINSMSP: {
    label: 'eINS',
    accent: '#ef6c00',
    background: 'rgba(239, 108, 0, 0.06)'
  }
};

const normalizeNodeHealth = (status) => {
  const normalized = String(status || '').toLowerCase();
  if (['active', 'running', 'online', 'healthy'].includes(normalized)) return 'healthy';
  if (['partial', 'warning', 'degraded'].includes(normalized)) return 'warning';
  return 'offline';
};

const healthColor = (health) => {
  if (health === 'healthy') return '#2e7d32';
  if (health === 'warning') return '#ed6c02';
  return '#d32f2f';
};

const healthLabel = (health) => {
  if (health === 'healthy') return t("Healthy");
  if (health === 'warning') return t("Degraded");
  return t("Offline");
};

const compactNodeName = (name) => {
  if (!name) return '-';
  return name.replace('.example.com', '');
};

const formatKstDateTime = (timestamp) => {
  if (!timestamp) return t('No indexed block timestamp');
  return new Date(timestamp).toLocaleString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Asia/Seoul'
  });
};

const formatDuration = (seconds) => {
  if (!Number.isFinite(seconds)) return '-';
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
};

const statusChipColor = (status) => {
  if (['up', 'listening', 'healthy'].includes(String(status || '').toLowerCase())) return 'success';
  if (['not_collected', 'not_listening', 'unknown'].includes(String(status || '').toLowerCase())) return 'warning';
  return 'error';
};

const normalizeStatusLabel = (status) => {
  if (!status) return 'Unknown';
  return String(status).replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());
};

const surfaceCardSx = {
  borderRadius: 3,
  border: '1px solid rgba(148, 163, 184, 0.22)',
  background: 'linear-gradient(180deg, rgba(255,255,255,0.98), rgba(248,250,252,0.94))',
  boxShadow: '0 18px 42px rgba(15, 23, 42, 0.08)'
};

const HealthDot = ({ status, size = 10, title }) => {
  const health = normalizeNodeHealth(status);
  return (
    <Tooltip title={ui(title || healthLabel(health))}>
      <Box
        component="span"
        sx={{
          width: size,
          height: size,
          borderRadius: '50%',
          display: 'inline-block',
          backgroundColor: healthColor(health),
          boxShadow: `0 0 0 3px ${healthColor(health)}22`,
          flex: '0 0 auto'
        }}
      />
    </Tooltip>
  );
};

const SignalCard = ({ title, value, subtitle, status = 'up' }) => (
  <Box
    sx={{
      border: '1px solid',
      borderColor: 'rgba(148, 163, 184, 0.25)',
      borderRadius: 2.5,
      p: 1.75,
      height: '100%',
      background: 'linear-gradient(180deg, #ffffff, #f8fafc)',
      boxShadow: '0 10px 26px rgba(15, 23, 42, 0.05)',
      transition: 'transform 160ms ease, box-shadow 160ms ease, border-color 160ms ease',
      '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: '0 16px 32px rgba(15, 23, 42, 0.09)',
        borderColor: 'rgba(37, 99, 235, 0.26)'
      }
    }}
  >
    <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
      <Typography variant="body2" color="text.secondary">
        {ui(title)}
      </Typography>
      <Chip
        label={ui(normalizeStatusLabel(status))}
        color={statusChipColor(status)}
        size="small"
        variant="outlined"
      />
    </Stack>
    <Typography variant="h6" sx={{ mt: 1.1, fontWeight: 800, wordBreak: 'break-word', color: '#0f172a' }}>
      {value}
    </Typography>
    <Typography variant="caption" color="text.secondary">
      {ui(subtitle)}
    </Typography>
  </Box>
);

const getOrganizationMeta = (organizationId) => ORGANIZATION_META[organizationId] || {
  label: mapOrganizationName(organizationId),
  accent: '#475569',
  background: 'rgba(71, 85, 105, 0.06)'
};

const getOrganizationDescription = (organizationId) => {
  if (organizationId === 'KULSMSP') return t("Integrated Platform");
  if (organizationId === 'KAISTMSP') return t("AI-based Container Placement Optimization");
  if (organizationId === 'EINSMSP') return t("Digital Twin Vehicle Dispatch Optimization");
  return t("Peer Organization");
};

const getOrganizationIcon = (organizationId, size = 'small') => {
  if (organizationId === 'KULSMSP') return <InstitutionIcon fontSize={size} />;
  if (organizationId === 'EINSMSP') return <TruckIcon fontSize={size} />;
  if (organizationId === 'KAISTMSP') return <ComputerIcon fontSize={size} />;
  return <WorkstationIcon fontSize={size} />;
};

const getOrganizationHealth = (nodes = []) => {
  const healthyCount = nodes.filter((node) => normalizeNodeHealth(node.status) === 'healthy').length;
  if (nodes.length === 0) return { health: 'offline', healthyCount };
  if (healthyCount === nodes.length) return { health: 'healthy', healthyCount };
  if (healthyCount > 0) return { health: 'warning', healthyCount };
  return { health: 'offline', healthyCount };
};

const StatusPill = ({ status, label, compact = false }) => {
  const health = normalizeNodeHealth(status);
  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={0.65}
      sx={{
        px: compact ? 0 : 0.85,
        py: compact ? 0 : 0.35,
        borderRadius: 999,
        backgroundColor: compact ? 'transparent' : '#ffffff',
        border: compact ? 'none' : '1px solid rgba(148, 163, 184, 0.26)',
        color: healthColor(health),
        flex: '0 0 auto'
      }}
    >
      <HealthDot status={health} size={7} />
      <Typography variant="caption" sx={{ fontWeight: 800, lineHeight: 1, whiteSpace: 'nowrap' }}>
        {ui(label || healthLabel(health))}
      </Typography>
    </Stack>
  );
};

const TopologyMetric = ({ icon, title, value, subtitle, status }) => (
  <Box
    sx={{
      minWidth: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 1.25,
      minHeight: 84,
      px: { xs: 1.25, md: 1.6 },
      py: 1.25,
      border: '1px solid rgba(148, 163, 184, 0.24)',
      borderRadius: 2.5,
      background: '#ffffff',
      boxShadow: '0 10px 24px rgba(15, 23, 42, 0.05)'
    }}
  >
    <Box
      sx={{
        width: 42,
        height: 42,
        borderRadius: 1.8,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#0f172a',
        background: '#ffffff',
        border: '1px solid rgba(148, 163, 184, 0.28)',
        boxShadow: '0 8px 18px rgba(15, 23, 42, 0.06)',
        flex: '0 0 auto'
      }}
    >
      {icon}
    </Box>
    <Box sx={{ minWidth: 0, textAlign: 'left' }}>
      <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 800, display: 'block', lineHeight: 1.15 }}>
        {ui(title)}
      </Typography>
      <Stack direction="row" alignItems="center" spacing={0.9}>
        {status && <HealthDot status={status} size={8} />}
        <Typography variant="h5" sx={{ fontWeight: 900, color: status ? healthColor(normalizeNodeHealth(status)) : '#0f172a', lineHeight: 1.08 }}>
          {value}
        </Typography>
      </Stack>
      {subtitle && (
        <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 700, display: 'block', mt: 0.2, lineHeight: 1.15 }}>
          {ui(subtitle)}
        </Typography>
      )}
    </Box>
  </Box>
);

const PeerNodeRow = ({ node, compact = false }) => {
  const health = normalizeNodeHealth(node?.status);
  const NodeIcon = node?.type === 'Orderer' ? ServerIcon : ComputerIcon;
  return (
    <Box
      sx={{
        border: '1px solid rgba(148, 163, 184, 0.28)',
        borderRadius: compact ? 1.4 : 1.8,
        px: compact ? 1 : 1.15,
        py: compact ? 0.85 : 1,
        background: '#ffffff',
        minHeight: compact ? 50 : 58,
        boxShadow: compact ? 'none' : '0 8px 18px rgba(15, 23, 42, 0.05)'
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1.15}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
          <Box
            sx={{
              width: compact ? 28 : 32,
              height: compact ? 28 : 32,
              borderRadius: 1.4,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'rgba(15, 23, 42, 0.04)',
              color: '#334155',
              border: '1px solid rgba(148, 163, 184, 0.24)',
              flex: '0 0 auto'
            }}
          >
            <NodeIcon fontSize="small" />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography
              variant="body2"
              fontWeight={900}
              title={node?.name || node?.id}
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                color: '#0f172a'
              }}
            >
              {compactNodeName(node?.name || node?.id)}
            </Typography>
            <Typography
              variant="caption"
              title={node?.role || '-'}
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                color: '#64748b'
              }}
            >
              {node?.role || '-'}
            </Typography>
          </Box>
        </Stack>
        <Stack direction="row" alignItems="center" spacing={0.85} sx={{ flex: '0 0 auto' }}>
          {compact ? (
            <StatusPill status={node?.status} label={ui(healthLabel(health))} compact />
          ) : (
            <HealthDot status={health} size={8} title={ui(healthLabel(health))} />
          )}
          <Typography variant="caption" sx={{ fontWeight: 800, color: '#64748b', minWidth: compact ? 62 : 70, textAlign: 'right' }}>
            {node?.ports?.[0] || t("registered")}
          </Typography>
        </Stack>
      </Stack>
    </Box>
  );
};

const OrganizationBox = ({ organization }) => {
  const meta = getOrganizationMeta(organization.id);
  const { health: orgHealth, healthyCount } = getOrganizationHealth(organization.nodes);

  return (
    <Box
      sx={{
        height: '100%',
        border: '1px solid rgba(148, 163, 184, 0.34)',
        borderRadius: 2.5,
        background: '#ffffff',
        boxShadow: '0 14px 32px rgba(15, 23, 42, 0.07)',
        p: 1.45,
        minWidth: 0
      }}
    >
      <Stack
        direction="row"
        alignItems="flex-start"
        justifyContent="space-between"
        spacing={1.25}
        sx={{ mb: 1.3, minHeight: 56 }}
      >
        <Stack direction="row" alignItems="center" spacing={1.05} sx={{ minWidth: 0 }}>
          <Box
            sx={{
              width: 42,
              height: 42,
              borderRadius: 2,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: meta.background,
              border: `1px solid ${meta.accent}22`,
              color: meta.accent,
              flex: '0 0 auto'
            }}
          >
            {getOrganizationIcon(organization.id)}
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle1" fontWeight={900} noWrap sx={{ color: '#0f172a', lineHeight: 1.16 }}>
              {ui(meta.label)}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: '#64748b',
                fontWeight: 700,
                display: '-webkit-box',
                lineHeight: 1.25,
                minHeight: 32,
                overflow: 'hidden',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical'
              }}
            >
              {ui(getOrganizationDescription(organization.id))}
            </Typography>
          </Box>
        </Stack>
        <StatusPill status={orgHealth} label={t("{0}/{1} healthy", [healthyCount, organization.nodes.length])} />
      </Stack>

      <Stack spacing={0.9}>
        {organization.nodes.map((node) => (
          <PeerNodeRow key={node.id || node.name} node={node} />
        ))}
      </Stack>
    </Box>
  );
};

const OrdererServiceCard = ({ orderers = [] }) => {
  const health = orderers.some((node) => normalizeNodeHealth(node.status) === 'healthy') ? 'healthy' : 'offline';
  const orderer = orderers[0] || {};
  return (
    <Box
      sx={{
        width: { xs: '100%', sm: 360 },
        mx: 'auto',
        border: '1px solid rgba(148, 163, 184, 0.42)',
        borderRadius: 2.5,
        background: '#ffffff',
        boxShadow: '0 14px 30px rgba(15, 23, 42, 0.07)',
        p: 1.6
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1.35}>
        <Box
          sx={{
            width: 54,
            height: 54,
            borderRadius: 2,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#0f172a',
            border: '1px solid rgba(15, 23, 42, 0.18)',
            background: '#f8fafc',
            flex: '0 0 auto'
          }}
        >
          <ServerIcon />
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
            <Typography variant="subtitle1" sx={{ fontWeight: 900, color: '#0f172a' }}>{t("Orderer Service")}</Typography>
            <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 800 }}>
              {orderers.length || 0}{" "}{t("node")}</Typography>
          </Stack>
          <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1} sx={{ mt: 1 }}>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" sx={{ fontWeight: 900, color: '#0f172a' }}>
                {compactNodeName(orderer.name || orderer.id || 'orderer')}
              </Typography>
              <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700 }}>
                {orderer.ports?.[0] || '7050:7050'}
              </Typography>
            </Box>
            <StatusPill status={health} compact />
          </Stack>
        </Box>
      </Stack>
    </Box>
  );
};

const ChannelBackboneCard = ({ height, health }) => (
  <Box
    sx={{
      width: { xs: '100%', md: '76%' },
      mx: 'auto',
      border: '1px solid rgba(96, 165, 250, 0.45)',
      borderRadius: 2.5,
      background: 'linear-gradient(180deg, rgba(255,255,255,0.98), rgba(248,250,252,0.98))',
      boxShadow: '0 14px 30px rgba(37, 99, 235, 0.08)',
      px: { xs: 1.5, md: 2.2 },
      py: 1.55
    }}
  >
    <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
      <Stack direction="row" alignItems="center" spacing={1.25} sx={{ minWidth: 0 }}>
        <Box
          sx={{
            width: 46,
            height: 46,
            borderRadius: 2,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#2563eb',
            background: 'rgba(37, 99, 235, 0.08)',
            border: '1px solid rgba(37, 99, 235, 0.18)',
            flex: '0 0 auto'
          }}
        >
          <ChainIcon />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 900, color: '#0f172a', lineHeight: 1.18 }}>{t("Channel Backbone")}</Typography>
          <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700 }}>{t("SOCFAI Fabric Network")}</Typography>
        </Box>
      </Stack>
      <Stack direction="row" alignItems="center" spacing={1.5}>
        <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 800 }}>
          #{height}
        </Typography>
        <StatusPill status={health} compact />
      </Stack>
    </Stack>
  </Box>
);

const LegendItem = ({ icon, label, sublabel }) => (
  <Stack direction="row" alignItems="center" spacing={1}>
    <Box sx={{ width: 28, height: 28, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: '#0f172a' }}>
      {icon}
    </Box>
    <Box>
      <Typography variant="body2" sx={{ color: '#334155', fontWeight: 800, lineHeight: 1.1 }}>
        {ui(label)}
      </Typography>
      {sublabel && (
        <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700 }}>
          {ui(sublabel)}
        </Typography>
      )}
    </Box>
  </Stack>
);

const SystemMonitor = () => {
  const [systemStatus, setSystemStatus] = useState({
    desktop1: { status: 'online', height: 0, hash: '', totalPeers: 0, peers: [] },
    desktop2: { status: 'online', height: 0, hash: '', totalPeers: 0, peers: [] },
    desktop3: { status: 'online', height: 0, hash: '', totalPeers: 0, peers: [] },
    orderers: [],
    organizations: [],
    sync: false,
    totalNodes: 0,
    totalOrganizations: 0,
    totalPeers: 0,
    activeNodes: 0,
    localDetectedNodes: 0,
    latestBlockTimestamp: '',
    consensusHealth: 'Healthy'
  });
  const [chaincodes, setChaincodes] = useState([]);
  const [monitorSnapshot, setMonitorSnapshot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // 🎯 Fetch chaincode information from real API
  const getAllInstalledChaincodes = async () => {
    try {
      const response = await webappFetch(`${getWebappApiUrl()}/api/monitoring/chaincodes`);
      const data = await response.json();

      if (data.success && data.data) {
        return data.data.map(chaincode => ({
          name: chaincode.name || 'unknown',
          version: chaincode.version || '1.0',
          sequence: chaincode.sequence || 1,
          status: 'Committed',
          channel: chaincode.channel || 'socfai-production',
          displayName: chaincode.name ? chaincode.name.charAt(0).toUpperCase() + chaincode.name.slice(1) : 'Unknown',
          installedAt: chaincode.installedAt || null
        }));
      }

      return [];
    } catch (error) {
      return [];
    }
  };

  // System status check
  const fetchSystemStatus = async () => {
    try {

      // Simultaneous node info, latest indexed block, and monitor snapshot retrieval
      const [nodeResponse, latestBlockResponse, monitorSnapshotResponse] = await Promise.all([
        webappFetch(`${getWebappApiUrl()}/api/blockchain-infra/node-management`),
        webappFetch(`${getWebappApiUrl()}/api/block-explorer/blocks?page=1&limit=1`),
        webappFetch(`${getWebappApiUrl()}/api/system/monitor-snapshot`)
      ]);

      const nodeData = await nodeResponse.json();
      const blocksData = await latestBlockResponse.json();
      const monitorSnapshotData = await monitorSnapshotResponse.json();

      if (nodeData.success && blocksData.success) {
        if (monitorSnapshotData.success) {
          setMonitorSnapshot(monitorSnapshotData.data);
        }

        const latestBlock = blocksData.data.blocks[0];
        const latestBlockHeight = latestBlock ? Number(latestBlock.blockNumber) : 0;
        const latestBlockHash = latestBlock ? latestBlock.blockHash : '';
        const latestBlockTimestamp = latestBlock ? latestBlock.timestamp : '';

        // 🎯 Extract organization-specific peer info from node data
        const organizationPeers = {};
        const ordererNodes = [];
        if (nodeData.data.registeredNodes && nodeData.data.registeredNodes.nodes) {
          nodeData.data.registeredNodes.nodes.forEach(node => {
            const displayNode = {
              id: node.id,
              name: node.name,
              type: node.type,
              organization: node.organization,
              organizationName: node.organizationName,
              role: node.role,
              status: node.status,
              location: node.location,
              ip: node.ip,
              ports: node.ports || [],
              lastSeen: node.lastSeen
            };

            if (node.type === 'Orderer') {
              ordererNodes.push(displayNode);
            }

            if (node.type === 'Peer') {
              if (!organizationPeers[node.organization]) {
                organizationPeers[node.organization] = [];
              }
              organizationPeers[node.organization].push(displayNode);
            }
          });
        }

        // 🎯 Configure peer information by Desktop
        const kulsPeers = organizationPeers['KULSMSP'] || [];
        const kaistPeers = organizationPeers['KAISTMSP'] || [];
        const einsPeers = organizationPeers['EINSMSP'] || [];
        const organizations = ORGANIZATION_ORDER.map((organizationId) => {
          const nodes = organizationPeers[organizationId] || [];
          const firstNode = nodes[0] || {};
          return {
            id: organizationId,
            organizationName: firstNode.organizationName || mapOrganizationName(organizationId),
            location: firstNode.location || '-',
            nodes
          };
        });

        const activePeers = kulsPeers.length + kaistPeers.length + einsPeers.length;
        const networkSummary = nodeData.data.networkSummary || {};
        const localDetectedNodes = nodeData.data.localDetection?.detectedNodes?.length || 0;
        const indexedHeight = monitorSnapshotData.success
          ? Number(monitorSnapshotData.data?.ledger?.indexedHeight || 0)
          : latestBlockHeight;
        const ledgerHeight = indexedHeight || latestBlockHeight;
        const syncStatus = activePeers > 0 && ledgerHeight > 0;

        setSystemStatus({
          desktop1: {
            status: kulsPeers.length > 0 ? 'online' : 'offline',
            height: ledgerHeight,
            hash: latestBlockHash,
            organization: 'KULS',
            orgName: 'Busan Container Terminal',
            totalPeers: kulsPeers.length,
            peers: kulsPeers,
            role: 'Container Terminal Management'
          },
          desktop2: {
            status: kaistPeers.length > 0 ? 'online' : 'offline',
            height: ledgerHeight,
            hash: latestBlockHash,
            organization: 'KAIST',
            orgName: 'Busan Port Yard',
            totalPeers: kaistPeers.length,
            peers: kaistPeers,
            role: 'Port Yard Management'
          },
          desktop3: {
            status: einsPeers.length > 0 ? 'online' : 'offline',
            height: ledgerHeight,
            hash: latestBlockHash,
            organization: 'eINS',
            orgName: 'Busan Container Transport',
            totalPeers: einsPeers.length,
            peers: einsPeers,
            role: 'Container Transport'
          },
          orderers: ordererNodes,
          organizations,
          sync: syncStatus,
          totalNodes: networkSummary.totalRegisteredNodes || 0,
          totalOrganizations: networkSummary.totalOrganizations || 0,
          totalPeers: activePeers,
          activeNodes: networkSummary.totalActivatedNodes || 0,
          localDetectedNodes,
          latestBlockTimestamp,
          consensusHealth: syncStatus ? 'Healthy' : 'Degraded'
        });
        setError('');

        // 🎯 Set all installed chaincodes (regardless of recent usage)
        const installedChaincodes = await getAllInstalledChaincodes();

        // 🎯 Update actual usage info from recent blocks
        if (blocksData.data.blocks && blocksData.data.blocks.length > 0) {
          const recentBlocks = await Promise.all([
            webappFetch(`${getWebappApiUrl()}/api/block-explorer/blocks?page=1&limit=50`),
          ]);

          const allRecentBlocks = await recentBlocks[0].json();

          if (allRecentBlocks.success && allRecentBlocks.data.blocks) {
            const usageMap = new Map();

            allRecentBlocks.data.blocks.forEach(block => {
              if (block.chaincode && block.chaincode.name) {
                const existing = usageMap.get(block.chaincode.name);
                if (!existing || new Date(block.timestamp) > new Date(existing.lastUpdate)) {
                  usageMap.set(block.chaincode.name, {
                    lastUpdate: block.timestamp,
                    lastBlockNumber: block.blockNumber
                  });
                }
              }
            });

            // Merge actual usage info with chaincodes
            installedChaincodes.forEach(chaincode => {
              const usage = usageMap.get(chaincode.name);
              if (usage) {
                chaincode.lastUpdate = usage.lastUpdate;
                chaincode.lastBlockNumber = usage.lastBlockNumber;
                chaincode.recentlyUsed = true;
              } else {
                chaincode.lastUpdate = null;
                chaincode.lastBlockNumber = null;
                chaincode.recentlyUsed = false;
              }
            });
          }
        }

        setChaincodes(installedChaincodes);
      } else {
        throw new Error('Unable to retrieve system data');
      }
    } catch (err) {
      setError(err.message || 'Unable to check system status');

      // Set default values on error
      setSystemStatus({
        desktop1: { status: 'offline', height: 0, hash: '', totalPeers: 3, peers: [], role: 'Container Terminal' },
        desktop2: { status: 'offline', height: 0, hash: '', totalPeers: 3, peers: [], role: 'Port Yard' },
        desktop3: { status: 'offline', height: 0, hash: '', totalPeers: 3, peers: [], role: 'Container Transport' },
        orderers: [],
        organizations: [],
        sync: false,
        totalNodes: 10,
        totalOrganizations: 0,
        totalPeers: 9,
        activeNodes: 0,
        localDetectedNodes: 0,
        latestBlockTimestamp: '',
        consensusHealth: 'Degraded'
      });


      // Show all chaincodes even on error (now async)
      getAllInstalledChaincodes().then(chaincodesData => {
        setChaincodes(chaincodesData);
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSystemStatus();
    const interval = setInterval(fetchSystemStatus, 30000); // Update every 30 seconds (optimized for external access)
    return () => clearInterval(interval);
  }, []);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ ml: 2 }}>{t("Loading data...")}</Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        flexGrow: 1,
        p: { xs: 2, md: 3 },
        minHeight: '100vh',
        background:
          'radial-gradient(circle at top left, rgba(37,99,235,0.08), transparent 32%), linear-gradient(180deg, #f8fafc 0%, #eef2f7 100%)'
      }}
    >
      <Box
        sx={{
          mb: 3,
          p: { xs: 2.25, md: 3 },
          borderRadius: 4,
          color: '#fff',
          background:
            'linear-gradient(135deg, #071827 0%, #12395b 48%, #0f766e 100%)',
          boxShadow: '0 22px 48px rgba(15, 23, 42, 0.22)',
          border: '1px solid rgba(255,255,255,0.16)',
          position: 'relative',
          overflow: 'hidden',
          '&::after': {
            content: '""',
            position: 'absolute',
            right: -90,
            top: -100,
            width: 260,
            height: 260,
            borderRadius: '50%',
            background: 'rgba(125, 211, 252, 0.16)'
          }
        }}
      >
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', md: 'center' }}
          spacing={2}
          sx={{ position: 'relative', zIndex: 1 }}
        >
          <Box>
            <Typography variant="overline" sx={{ letterSpacing: 0, color: 'rgba(226, 232, 240, 0.82)', fontWeight: 800 }}>{t("SOCFAI Fabric Operations")}</Typography>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 900, mt: 0.25 }}>{t("System Monitor")}</Typography>
            <Typography variant="body2" sx={{ color: 'rgba(226, 232, 240, 0.78)', mt: 0.75 }}>{t("Registry, ledger index, service signals, and committed chaincode definitions.")}</Typography>
          </Box>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Chip
              icon={<RefreshIcon />}
              label={t("Live Updates 30s")}
              sx={{
                color: '#dcfce7',
                borderColor: 'rgba(187, 247, 208, 0.48)',
                backgroundColor: 'rgba(22, 163, 74, 0.16)',
                '& .MuiChip-icon': { color: '#bbf7d0' }
              }}
              variant="outlined"
              size="small"
            />
            <Button
              variant="contained"
              size="small"
              startIcon={<RefreshIcon />}
              onClick={() => { setLoading(true); setError(null); fetchSystemStatus(); }}
              sx={{
                backgroundColor: 'rgba(255,255,255,0.92)',
                color: '#0f172a',
                boxShadow: 'none',
                fontWeight: 800,
                '&:hover': {
                  backgroundColor: '#fff',
                  boxShadow: '0 10px 24px rgba(15, 23, 42, 0.22)'
                }
              }}
            >{t("Refresh")}</Button>
          </Stack>
        </Stack>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {uiError(error)}
        </Alert>
      )}

      {/* Network topology */}
      <Card
        sx={{
          mb: 4,
          borderRadius: 3,
          border: '1px solid rgba(148, 163, 184, 0.24)',
          background: '#ffffff',
          boxShadow: '0 20px 48px rgba(15, 23, 42, 0.08)',
          overflow: 'hidden'
        }}
      >
        <CardContent sx={{ p: { xs: 2, md: 2.75 }, '&:last-child': { pb: { xs: 2, md: 2.75 } } }}>
          <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }} spacing={1.5} sx={{ mb: 2.5 }}>
            <Box>
              <Typography variant="overline" sx={{ color: '#475569', fontWeight: 900, letterSpacing: 0 }}>{t("Network / Topology")}</Typography>
              <Typography variant="h4" component="h2" sx={{ fontWeight: 900, color: '#0f172a', lineHeight: 1.12 }}>{t("Fabric Network Topology")}</Typography>
              <Typography variant="body2" sx={{ color: '#64748b', mt: 0.4 }}>{t("Hyperledger Fabric network architecture and node registry status.")}</Typography>
            </Box>
            <Chip
              icon={<LanIcon />}
              label={t("{0} organizations / {1} peers", [systemStatus.totalOrganizations, systemStatus.totalPeers])}
              variant="outlined"
              sx={{
                color: '#1e3a8a',
                borderColor: 'rgba(37, 99, 235, 0.24)',
                backgroundColor: 'rgba(239, 246, 255, 0.92)',
                fontWeight: 800,
                '& .MuiChip-icon': { color: '#2563eb' }
              }}
            />
          </Stack>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(4, minmax(0, 1fr))' },
              gap: { xs: 1.25, md: 1.5 },
              alignItems: 'stretch',
              mb: 2.6
            }}
          >
            <TopologyMetric
              icon={<ComputerIcon fontSize="small" />}
              title={t("Channel Height")}
              value={`#${systemStatus.desktop1.height}`}
              subtitle={t("Indexed #{0}", [monitorSnapshot?.ledger?.indexedHeight ?? systemStatus.desktop1.height])}
            />
            <TopologyMetric
              icon={<LanIcon fontSize="small" />}
              title={t("Active Peers")}
              value={systemStatus.totalPeers}
              subtitle={t("{0}/{1} registered active", [systemStatus.activeNodes, systemStatus.totalNodes])}
            />
            <TopologyMetric
              icon={<StorageIcon fontSize="small" />}
              title={t("Indexed Lag")}
              value={monitorSnapshot?.ledger?.indexLag ?? 0}
              subtitle={t("Fabric #{0}", [monitorSnapshot?.ledger?.fabricLatestBlock ?? systemStatus.desktop1.height])}
            />
            <TopologyMetric
              icon={<ChainIcon fontSize="small" />}
              title={t("Network Health")}
              value={ui(systemStatus.consensusHealth)}
              status={systemStatus.sync ? 'healthy' : 'warning'}
            />
          </Box>

          <Box
            sx={{
              position: 'relative',
              borderRadius: 3,
              p: { xs: 1.5, md: 2 },
              background: '#ffffff',
              border: '1px solid rgba(148, 163, 184, 0.22)',
              overflow: 'hidden',
              '&::before': {
                content: '""',
                position: 'absolute',
                inset: 0,
                backgroundImage:
                  'radial-gradient(circle at 1px 1px, rgba(100,116,139,0.18) 1px, transparent 0)',
                backgroundSize: '22px 22px',
                maskImage: 'linear-gradient(180deg, rgba(0,0,0,0.7), rgba(0,0,0,0.18))',
                pointerEvents: 'none'
              }
            }}
          >
            <Box
              sx={{
                position: 'relative',
                zIndex: 1,
                display: 'block'
              }}
            >
              <Box
                sx={{
                  position: 'relative',
                  border: '1px solid rgba(148, 163, 184, 0.24)',
                  borderRadius: 2.5,
                  background: 'rgba(255, 255, 255, 0.92)',
                  p: { xs: 1.5, md: 2.1 },
                  minHeight: 560,
                  overflow: 'hidden'
                }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 900, color: '#0f172a', textTransform: 'uppercase', mb: 1.4 }}>{t("Network Topology")}</Typography>
                <OrdererServiceCard orderers={systemStatus.orderers || []} />
                <Box
                  sx={{
                    height: 44,
                    position: 'relative',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      left: '50%',
                      top: 0,
                      bottom: 0,
                      borderLeft: '2px solid rgba(15, 23, 42, 0.32)'
                    }
                  }}
                />
                <ChannelBackboneCard
                  height={systemStatus.desktop1.height}
                  health={systemStatus.sync ? 'healthy' : 'warning'}
                />
                <Box
                  sx={{
                    height: 62,
                    position: 'relative',
                    display: { xs: 'none', md: 'block' },
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      left: '50%',
                      top: 0,
                      height: 30,
                      borderLeft: '2px solid rgba(15, 23, 42, 0.32)'
                    },
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      left: '16.5%',
                      right: '16.5%',
                      top: 30,
                      borderTop: '2px solid rgba(15, 23, 42, 0.32)'
                    }
                  }}
                >
                  {[16.5, 50, 83.5].map((left) => (
                    <Box
                      key={left}
                      sx={{
                        position: 'absolute',
                        left: `${left}%`,
                        top: 30,
                        height: 32,
                        borderLeft: '2px solid rgba(15, 23, 42, 0.32)'
                      }}
                    />
                  ))}
                </Box>
                <Box sx={{ display: { xs: 'block', md: 'none' }, height: 20 }} />
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' },
                    gap: 1.8
                  }}
                >
                  {(systemStatus.organizations || []).map((organization) => (
                    <OrganizationBox key={organization.id} organization={organization} />
                  ))}
                </Box>
              </Box>
            </Box>
          </Box>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1fr) 420px' },
              gap: 2,
              mt: 2
            }}
          >
            <Box
              sx={{
                border: '1px solid rgba(148, 163, 184, 0.24)',
                borderRadius: 2.5,
                background: '#ffffff',
                px: 1.7,
                py: 1.35,
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', md: 'repeat(4, max-content)' },
                gap: { xs: 1.2, md: 3 },
                alignItems: 'center'
              }}
            >
              <LegendItem icon={<HealthDot status="healthy" size={11} />} label={t("Healthy")} sublabel={t("Node is operational")} />
              <LegendItem icon={<ComputerIcon />} label={t("Peer Node")} sublabel={t("Endorsing Peer")} />
              <LegendItem icon={<ServerIcon />} label={t("Orderer Node")} sublabel={t("Ordering Service")} />
              <LegendItem icon={<ChainIcon />} label={t("Channel Backbone")} sublabel={t("Shared Network Layer")} />
            </Box>
            <Box
              sx={{
                border: '1px solid rgba(148, 163, 184, 0.24)',
                borderRadius: 2.5,
                background: '#ffffff',
                px: 1.7,
                py: 1.35
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 900, color: '#0f172a', mb: 1 }}>{t("Network Summary")}</Typography>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
                  gap: 1.2,
                  alignItems: 'center'
                }}
              >
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 900, color: '#0f172a', lineHeight: 1 }}>
                    {(systemStatus.orderers || []).length}
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700 }}>{t("Orderer")}</Typography>
                </Box>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 900, color: '#0f172a', lineHeight: 1 }}>
                    {(systemStatus.organizations || []).length}
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700 }}>{t("Organizations")}</Typography>
                </Box>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 900, color: '#0f172a', lineHeight: 1 }}>
                    {systemStatus.totalPeers}
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700 }}>{t("Peers")}</Typography>
                </Box>
                <Stack direction="row" alignItems="center" spacing={0.8}>
                  <HealthDot status={systemStatus.sync ? 'healthy' : 'warning'} size={9} />
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 900, color: healthColor(systemStatus.sync ? 'healthy' : 'warning'), lineHeight: 1.1 }}>
                      {ui(systemStatus.consensusHealth)}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700 }}>{t("Network")}</Typography>
                  </Box>
                </Stack>
              </Box>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Monitoring signals */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12}>
          <Card sx={surfaceCardSx}>
            <CardContent>
              <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={1}>
                <Box>
                  <Typography variant="h6" component="h2" sx={{ fontWeight: 800 }}>{t("Monitoring Signals")}</Typography>
                  <Typography variant="body2" color="text.secondary">{t("Service health and index signals checked by the running WebApp backend.")}</Typography>
                </Box>
                <Chip
                  label={t("Generated {0}", [formatKstDateTime(monitorSnapshot?.generatedAt)])}
                  size="small"
                  variant="outlined"
                  color="info"
                />
              </Stack>
              <Alert
                severity="warning"
                sx={{
                  mt: 2,
                  borderRadius: 2,
                  border: '1px solid rgba(245, 158, 11, 0.25)',
                  backgroundColor: 'rgba(255, 251, 235, 0.72)'
                }}
              >
                <Typography variant="body2">{t("Remote desktop Docker health, host CPU load, and network latency are not collected by the current WebApp backend. This section shows only values that are directly checked or indexed by the running system.")}</Typography>
              </Alert>

              <Box sx={{ mt: 3 }}>
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  <Grid item xs={12} sm={6} md={4}>
                    <SignalCard
                      title={t("API Gateway")}
                      value={monitorSnapshot?.services?.apiGateway?.reportedStatus || t('Not available')}
                      subtitle={t("latency {0}ms / redis {1} / fabric {2}", [monitorSnapshot?.services?.apiGateway?.latencyMs ?? '-', monitorSnapshot?.services?.apiGateway?.dependencies?.redis?.status || '-', monitorSnapshot?.services?.apiGateway?.dependencies?.fabric?.status || '-'])}
                      status={monitorSnapshot?.services?.apiGateway?.status || 'unknown'}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <SignalCard
                      title={t("WebApp Backend")}
                      value={formatDuration(monitorSnapshot?.services?.webappBackend?.uptimeSeconds)}
                      subtitle={t("{0} MB heap / {1} WebSocket clients", [monitorSnapshot?.services?.webappBackend?.memory?.heapUsedMb ?? '-', monitorSnapshot?.websocket?.connectedClients ?? 0])}
                      status={monitorSnapshot?.services?.webappBackend?.status || 'unknown'}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <SignalCard
                      title={t("PostgreSQL Index")}
                      value={`#${monitorSnapshot?.ledger?.indexedHeight ?? systemStatus.desktop1.height}`}
                      subtitle={t("{0} indexed transactions / {1}ms", [monitorSnapshot?.ledger?.indexedTransactions ?? '-', monitorSnapshot?.services?.postgres?.latencyMs ?? '-'])}
                      status={monitorSnapshot?.services?.postgres?.status || 'unknown'}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <SignalCard
                      title={t("CouchDB State DB")}
                      value={monitorSnapshot?.services?.couchDb?.reportedStatus || t('Not available')}
                      subtitle={t("latency {0}ms", [monitorSnapshot?.services?.couchDb?.latencyMs ?? '-'])}
                      status={monitorSnapshot?.services?.couchDb?.status || 'unknown'}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <SignalCard
                      title={t("Fabric Event Listener")}
                      value={t("{0} listener", [monitorSnapshot?.services?.fabricListener?.activeListeners ?? 0])}
                      subtitle={t("initialized {0} / listening {1}", [monitorSnapshot?.services?.fabricListener?.isInitialized ? 'yes' : 'no', monitorSnapshot?.services?.fabricListener?.isListening ? 'yes' : 'no'])}
                      status={monitorSnapshot?.services?.fabricListener?.status || 'unknown'}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <SignalCard
                      title={t("Index Lag")}
                      value={t("{0} block", [monitorSnapshot?.ledger?.indexLag ?? 0])}
                      subtitle={t("Fabric #{0} / Indexed #{1}", [monitorSnapshot?.ledger?.fabricLatestBlock ?? '-', monitorSnapshot?.ledger?.indexedHeight ?? '-'])}
                      status={(monitorSnapshot?.ledger?.indexLag ?? 0) === 0 ? 'up' : 'unknown'}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <SignalCard
                      title={t("Local Docker Detection")}
                      value={systemStatus.localDetectedNodes}
                      subtitle={t("orderer/KULS containers detected on this desktop")}
                      status={systemStatus.localDetectedNodes > 0 ? 'up' : 'unknown'}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <SignalCard
                      title={t("Remote Runtime Health")}
                      value={t("Not collected")}
                      subtitle={t("requires a remote health endpoint or agent")}
                      status="not_collected"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <SignalCard
                      title={t("Refresh Interval")}
                      value="30s"
                      subtitle={formatKstDateTime(monitorSnapshot?.generatedAt)}
                      status="up"
                    />
                  </Grid>
                </Grid>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Chaincode definitions */}
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Card sx={surfaceCardSx}>
            <CardContent>
              <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={1.5} sx={{ mb: 2 }}>
                <Box>
                  <Typography variant="h6" component="h2" sx={{ fontWeight: 800 }}>{t("Chaincode Definitions")}</Typography>
                  <Typography variant="body2" color="text.secondary">{t("Committed definitions from peer lifecycle query; recent usage is derived from indexed blocks.")}</Typography>
                </Box>
                <Chip label={t("{0} definitions", [chaincodes.length])} color="primary" variant="outlined" size="small" />
              </Stack>

              <TableContainer
                component={Paper}
                sx={{
                  borderRadius: 2.5,
                  border: '1px solid rgba(148, 163, 184, 0.22)',
                  boxShadow: 'none',
                  overflow: 'hidden'
                }}
              >
                <Table sx={{ '& th': { fontWeight: 800, backgroundColor: '#f8fafc' } }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>{t("Chaincode Name")}</TableCell>
                      <TableCell>{t("Version")}</TableCell>
                      <TableCell>{t("Sequence")}</TableCell>
                      <TableCell>{t("Channel")}</TableCell>
                      <TableCell>{t("Definition Status")}</TableCell>
                      <TableCell>{t("Recent Indexed Usage")}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {chaincodes.length > 0 ? chaincodes.map((chaincode, index) => (
                      <TableRow key={index}>
                        <TableCell>
                          <Box display="flex" alignItems="center">
                            <StorageIcon
                              fontSize="small"
                              color={chaincode.recentlyUsed ? "primary" : "disabled"}
                              sx={{ mr: 1 }}
                            />
                            <Box>
                              <Typography variant="body2" fontWeight="bold">
                                {chaincode.displayName}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {chaincode.name}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={chaincode.version}
                            size="small"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>{chaincode.sequence}</TableCell>
                        <TableCell>
                          <Chip
                            label={chaincode.channel}
                            size="small"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={ui(chaincode.status)}
                            color="success"
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          {chaincode.lastUpdate ? (
                            <Box>
                              <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                                {new Date(chaincode.lastUpdate).toLocaleDateString('en-US', {
                                  year: 'numeric',
                                  month: '2-digit',
                                  day: '2-digit',
                                  hour: '2-digit',
                                  minute: '2-digit',
                                  timeZone: 'Asia/Seoul'
                                })}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>{t("Block #")}{chaincode.lastBlockNumber}
                              </Typography>
                              <Chip
                                label={t("Indexed Usage")}
                                color="primary"
                                size="small"
                                sx={{ mt: 0.5 }}
                              />
                            </Box>
                          ) : (
                            <Box>
                              <Typography variant="body2" color="text.secondary">{t("No recent indexed usage")}</Typography>
                              <Chip
                                label={t("Committed")}
                                color="default"
                                size="small"
                                sx={{ mt: 0.5 }}
                              />
                            </Box>
                          )}
                        </TableCell>
                      </TableRow>
                    )) : (
                      <TableRow>
                        <TableCell colSpan={6} align="center">{t("Unable to load chaincode information.")}</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  <strong>{t("Chaincode Definition Status")}</strong>: {chaincodes.length}{" "}{t("committed definitions were returned from the current monitoring API. This section does not infer per-organization runtime endorsement health.")}</Typography>
              </Alert>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default SystemMonitor;
